import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/get-user-color';
import { Skeleton } from '@/shared/components/ui/skeleton';
import type { BoardActivitySummaryDto } from '../../types';

interface BoardActivitySummaryPanelProps {
  summary: BoardActivitySummaryDto | null;
  isLoading: boolean;
}

export function BoardActivitySummaryPanel({ summary, isLoading }: BoardActivitySummaryPanelProps) {
  const { t } = useTranslation('tasks');

  if (isLoading) {
    return (
      <div
        className="flex w-full flex-col gap-4 lg:w-[340px]"
        data-testid="tasks-board-activity-summary-panel"
      >
        <Skeleton className="h-40 w-full rounded-[10px]" />
        <Skeleton className="h-32 w-full rounded-[10px]" />
      </div>
    );
  }

  const perPerson = summary?.perPerson ?? [];
  const maxCount = Math.max(1, ...perPerson.map((p) => p.count));

  return (
    <div
      className="flex w-full flex-col gap-4 lg:w-[340px]"
      data-testid="tasks-board-activity-summary-panel"
    >
      <div className="flex flex-col gap-3.5 rounded-[10px] border border-border bg-background px-4.5 py-4">
        <p className="text-[13px] font-medium text-foreground">{t('activity.summary.title')}</p>

        {!summary || summary.totalChanges === 0 ? (
          <p className="text-[13px] text-muted-foreground">{t('activity.summary.noActivity')}</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-[13px] text-muted-foreground">
                {t('activity.summary.totalChanges')}
              </p>
              <p className="text-sm font-medium text-foreground">{summary.totalChanges}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-[13px] text-muted-foreground">
                {t('activity.summary.completedTasks')}
              </p>
              <p className="text-sm font-medium text-success">{summary.completedTasks}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-[13px] text-muted-foreground">
                {t('activity.summary.newComments')}
              </p>
              <p className="text-sm font-medium text-primary">{summary.newComments}</p>
            </div>
            {summary.mostActiveUser && (
              <div className="flex items-center justify-between">
                <p className="text-[13px] text-muted-foreground">
                  {t('activity.summary.mostActive')}
                </p>
                <p className="text-sm font-medium text-foreground">{summary.mostActiveUser.name}</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-2.5 rounded-[10px] border border-border bg-background px-4.5 py-4">
        <p className="text-[13px] font-medium text-foreground">{t('activity.byPerson.title')}</p>

        {perPerson.length === 0 ? (
          <p
            className="text-[13px] text-muted-foreground"
            data-testid="tasks-board-activity-by-person-empty"
          >
            {t('activity.byPerson.empty')}
          </p>
        ) : (
          perPerson.map((person) => (
            <div
              key={person.userId}
              className="flex items-center gap-2"
              data-testid="tasks-board-activity-by-person-row"
            >
              <UserAvatar
                name={person.name ?? ''}
                color={person.color ?? getUserColor(person.userId)}
                src={person.avatarUrl ?? undefined}
                size="xs"
              />
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${(person.count / maxCount) * 100}%` }}
                />
              </div>
              <p className="shrink-0 text-xs font-medium text-muted-foreground">{person.count}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
